import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, Subscription, from, of } from 'rxjs';
import { catchError, filter, map, mergeMap, scan } from 'rxjs/operators';
import { createPlatformIconDataUrl } from '../link-analysis/platform-icon.util';
import { annotateImagesWithFaceDetection, isFaceDetectionPending, toAbsoluteOsintImageUrl } from '../osint-search/image-search-from-result.util';
import { OsintSearchService } from '../osint-search/osint-search.service';
import { UtilsService } from '../utils/utils.service';
import {
  canonicalFieldKey,
  collectMergedPlatformDisplayFields,
  displayLabelForFieldKey,
  MergedPlatformField,
} from '../../utils/canonical-field-key';

export interface SearchImage {
  platform: string;
  src: string;
  name: string;
  height: number;
  icon?: string;
  /** Original http(s)/data URL used for face detection (blob display src is not usable by API). */
  detectUrl?: string;
  /** true only after local face detection confirms a human face. */
  faceDetected?: boolean;
}

/** Shown when a profile photo URL exists but the browser cannot display it. */
export const DEFAULT_PROFILE_AVATAR_SRC =
  'data:image/svg+xml;charset=utf-8,' +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 128 128">
      <circle cx="64" cy="48" r="22" fill="#94a3b8"/>
      <path d="M28 118c6-28 20-42 36-42s30 14 36 42" fill="#94a3b8"/>
    </svg>`,
  );

@Injectable({
  providedIn: 'root',
})
export class SearchResultProcessorService {
  private blobUrls: Set<string> = new Set();

  constructor(
    private http: HttpClient,
    private osintSearchService: OsintSearchService,
    private utilsService: UtilsService,
  ) { }

  process(searchResult: any): any {
    const sources = this.extractSources(searchResult);
    const platforms = this.buildPlatformResults(sources);
    const images = this.buildImageResults(searchResult);

    // Add carrier information if available
    const carrierPlatforms = this.buildCarrierPlatforms(searchResult);
    const allPlatforms = [...platforms, ...carrierPlatforms];

    return { platforms: allPlatforms, images };
  }

  /**
   * Extract OSINT result images and annotate each with local face detection.
   * Used by email / phone / name / username / gst / printable result pages.
   * When the backend already returned `faceDetected`, those images skip the detect API.
   */
  extractImagesFromSources(apiResponseData: any): Observable<SearchImage[]> {
    const immediateImages: SearchImage[] = [];
    const imageUrls: string[] = [];
    const imageMetadata: Array<{
      platform: string;
      icon: string;
      url: string;
    }> = [];

    const faceDetectedMap = this.resolveFaceDetectedMap(apiResponseData);
    const faceDetectionPending = isFaceDetectionPending(apiResponseData);

    const dataList = apiResponseData?.data_list;

    if (dataList && dataList.length > 0) {
      const firstItem = dataList[0];

      // Extract images from image_url array
      if (Array.isArray(firstItem.image_url) && firstItem.image_url.length > 0) {
        firstItem.image_url.forEach((imageUrl: string, index: number) => {
          if (typeof imageUrl === 'string' && imageUrl.trim()) {
            if (imageUrl.startsWith('data:image/')) {
              // Handle base64 images immediately
              const matchedPlatform = this.findMatchingPlatform(imageUrl);
              const icon = createPlatformIconDataUrl(matchedPlatform || 'image');
              const platform = this.toImageSourceDisplayName(matchedPlatform);

              immediateImages.push({
                platform: platform,
                src: imageUrl,
                name: platform,
                height: index === 0 ? 200 : 150,
                icon: icon,
                detectUrl: imageUrl,
              });
            } else if (
              imageUrl.startsWith('http') ||
              imageUrl.startsWith('/api/osint/image/') ||
              imageUrl.startsWith('/osint/image/')
            ) {
              // Relative /api/osint/image/... must use API host (environment.baseUrl), not :4200
              const fetchUrl = toAbsoluteOsintImageUrl(imageUrl);
              imageUrls.push(fetchUrl);
              const matchedPlatform = this.findMatchingPlatform(fetchUrl);
              const icon = createPlatformIconDataUrl(matchedPlatform || 'image');
              const platform = this.toImageSourceDisplayName(matchedPlatform);

              imageMetadata.push({
                platform: platform,
                icon: icon,
                url: fetchUrl,
              });
            }
          }
        });
      }
    }

    const fetched$: Observable<SearchImage[]> =
      imageUrls.length > 0
        ? this.fetchAuthorizedImages(imageUrls, imageMetadata).pipe(
          map((authorizedImages) => [...immediateImages, ...authorizedImages]),
        )
        : of(immediateImages);

    // Show images as soon as they arrive. Face detection only gates the
    // camera button, so it must not block the gallery.
    return new Observable<SearchImage[]>((subscriber) => {
      let latest = immediateImages;
      let annotateSub: Subscription | undefined;
      if (immediateImages.length) {
        subscriber.next(immediateImages);
      }
      const fetchSub = fetched$.subscribe({
        next: (images) => {
          latest = images;
          if (!subscriber.closed) {
            subscriber.next(images);
          }
        },
        error: (error) => subscriber.error(error),
        complete: () => {
          if (subscriber.closed) {
            return;
          }
          annotateSub = this.annotateImagesForFaceSearch(
            latest,
            faceDetectedMap,
            { faceDetectionPending },
          ).subscribe({
            next: (annotated) => subscriber.next(annotated),
            error: () => subscriber.complete(),
            complete: () => subscriber.complete(),
          });
        },
      });

      return () => {
        fetchSub.unsubscribe();
        annotateSub?.unsubscribe();
      };
    });
  }

  /** Read persisted faceDetected map from fetchResults / history payload. */
  resolveFaceDetectedMap(
    apiResponseData: any,
  ): Record<string, boolean> | null {
    const candidates = [
      apiResponseData?.faceDetected,
      apiResponseData?.data?.faceDetected,
      apiResponseData?.data_list?.[0]?.faceDetected,
      apiResponseData?.vendor?.faceDetected,
      apiResponseData?.extra_data?.faceDetected,
    ];
    for (const candidate of candidates) {
      if (candidate && typeof candidate === 'object' && !Array.isArray(candidate)) {
        return candidate as Record<string, boolean>;
      }
    }
    return null;
  }

  /**
   * Run local face detection for any image list (e.g. username vendor avatars).
   * Shared by all OSINT search result pages.
   * Pass `faceDetectedMap` from history to skip the detect API for known images.
   * Pass `faceDetectionPending` while the server is still computing flags.
   */
  annotateImagesForFaceSearch(
    images: SearchImage[],
    faceDetectedMap?: Record<string, boolean> | null,
    options?: { faceDetectionPending?: boolean },
  ): Observable<SearchImage[]> {
    return annotateImagesWithFaceDetection(
      this.osintSearchService,
      images,
      faceDetectedMap,
      options,
    );
  }

  private fetchAuthorizedImages(
    urls: string[],
    metadata: Array<{ platform: string; icon: string; url: string }>,
  ): Observable<SearchImage[]> {
    const authToken = this.getAuthToken();
    if (!authToken) {
      console.warn('No auth token available for image requests');
      return of([]);
    }

    const metadataMap = new Map<
      string,
      { platform: string; icon: string; url: string }
    >();
    metadata.forEach((meta) => {
      metadataMap.set(meta.url, meta);
    });

    const headers = new HttpHeaders({
      Authorization: `Bearer ${authToken}`,
    });

    const IMAGE_FETCH_CONCURRENCY = 6;

    return from(urls.map((url, index) => ({ url, index }))).pipe(
      mergeMap(({ url, index }) => {
        const meta = metadataMap.get(url);
        if (!meta) {
          return of(null);
        }

        return this.http.get(url, { headers, responseType: 'blob' }).pipe(
          map((blob) => {
            const blobUrl = URL.createObjectURL(blob);
            this.blobUrls.add(blobUrl);
            const image: SearchImage = {
              platform: meta.platform,
              src: blobUrl,
              name: meta.platform,
              height: index === 0 ? 200 : 150,
              icon: meta.icon,
              detectUrl: url,
            };
            return { image, index };
          }),
          catchError((error) => {
            console.error(`Failed to load image ${url}:`, error);
            return of(null);
          }),
        );
      }, IMAGE_FETCH_CONCURRENCY),
      scan((slots: Array<SearchImage | undefined>, loaded) => {
        if (!loaded) {
          return slots;
        }
        const next = slots.slice();
        next[loaded.index] = loaded.image;
        return next;
      }, [] as Array<SearchImage | undefined>),
      map((slots) => slots.filter((image): image is SearchImage => !!image)),
      filter((images) => images.length > 0),
    );
  }

  private getAuthToken(): string | null {
    const USER_PORTAL = localStorage.getItem('USER_PORTAL');
    const userPortal = USER_PORTAL ? JSON.parse(USER_PORTAL) : null;
    return userPortal?.loginDetails?.access_token || null;
  }

  cleanupBlobUrls(): void {
    this.blobUrls.forEach((url) => URL.revokeObjectURL(url));
    this.blobUrls.clear();
  }

  private extractSources(searchResult: any): Record<string, any> {
    if (!searchResult || typeof searchResult !== 'object') {
      return {};
    }

    if (searchResult.sources && typeof searchResult.sources === 'object') {
      return searchResult.sources;
    }

    const nestedResult = searchResult?.data?.data_list?.find(
      (item: any) => !!item && typeof item === 'object',
    );

    if (nestedResult) {
      return this.extractSources(nestedResult);
    }

    return {};
  }

  private buildPlatformResults(sources: Record<string, any>): any[] {
    return Object.entries(sources ?? {})
      .map(([platformKey, platformData]) =>
        this.createPlatformResult(platformKey, platformData),
      )
      .filter((platformResult) => platformResult.entries.length > 0);
  }

  private createPlatformResult(
    platformKey: string,
    platformData: any = {},
  ): any {
    const normalizedKey = this.normalizeKey(platformKey);
    const entries = this.createEntries(platformData);

    return {
      platform: this.toDisplayName(platformKey),
      platformKey,
      icon: this.resolveIcon(normalizedKey),
      entries,
      emptyAccount: this.isEmptyAccountPlatform(platformData),
    };
  }

  /** has_account true with no fields/accounts — show after real hits, before not-found. */
  private isEmptyAccountPlatform(platformData: any): boolean {
    if (!platformData || platformData.has_account !== true) {
      return false;
    }
    const fields = platformData.fields;
    const fieldsEmpty =
      !fields ||
      (typeof fields === 'object' &&
        !Array.isArray(fields) &&
        Object.keys(fields).length === 0);
    if (!fieldsEmpty) {
      return false;
    }
    const accounts = Array.isArray(platformData.accounts)
      ? platformData.accounts
      : [];
    if (accounts.length > 0) {
      return false;
    }
    if (this.isMeaningfulAccount(platformData.account)) {
      return false;
    }
    if (this.hasInlineAccountData(platformData)) {
      return false;
    }
    return true;
  }

  private createEntries(platformData: any = {}): any[] {
    const entries: any[] = [];
    const identityKeys = new Set(['name', 'email', 'username', 'id', 'profile_url']);
    const merged = collectMergedPlatformDisplayFields(platformData).filter(
      (item) => !this.isHiddenDisplayField(item.key),
    );
    const identity = merged.filter((item) => identityKeys.has(item.key));
    const rest = merged.filter((item) => !identityKeys.has(item.key));

    if (identity.length) {
      const details = identity
        .map((item) => this.createMergedDetail(item))
        .filter(Boolean);
      const accountEntry = this.buildEntry(
        'account',
        this.resolveMergedAccountLabel(identity),
        details,
      );
      if (accountEntry) {
        entries.push(accountEntry);
      }
    }

    for (const item of rest) {
      const detail = this.createMergedDetail(item);
      const fieldEntry = this.buildEntry('field', null, detail ? [detail] : []);
      if (fieldEntry) {
        entries.push(fieldEntry);
      }
    }

    if (!entries.length && platformData.has_account === false) {
      const noAccountEntry = this.createNoAccountEntry(platformData);
      if (noAccountEntry) {
        entries.push(noAccountEntry);
      }
    }

    if (!entries.length && platformData.has_account === true) {
      const accountExistsEntry = this.createAccountExistsEntry(platformData);
      if (accountExistsEntry) {
        entries.push(accountExistsEntry);
      }
    }

    return entries;
  }

  private createMergedDetail(item: MergedPlatformField): any {
    let displayValue = item.value;
    if (item.rawField && item.values.length <= 1) {
      const field = this.normalizeField(item.rawField);
      if (field) {
        displayValue = this.normalizeFieldDisplayValue(item.key, field.value);
      }
    } else {
      displayValue = this.normalizeFieldDisplayValue(item.key, item.value);
    }

    const displayType = Array.isArray(displayValue)
      ? 'list'
      : typeof displayValue === 'object' && displayValue !== null
        ? 'object'
        : undefined;

    return this.createDetail(
      item.label || displayLabelForFieldKey(item.key),
      displayValue,
      displayType,
      item.searchType,
    );
  }

  private resolveMergedAccountLabel(identity: MergedPlatformField[]): string | null {
    for (const key of ['name', 'username', 'id']) {
      const match = identity.find((item) => item.key === key && item.values.length);
      if (!match) continue;
      const first = match.values[0];
      if (first == null) continue;
      return String(first);
    }
    return 'Account Information';
  }

  private createAccountEntry(account: any, index?: number): any {
    if (!account || typeof account !== 'object') {
      return null;
    }

    // Extract name - handle both string and object formats
    const accountName = this.extractNestedValue(account.name);
    const nameSearchType = this.extractOsintSearchType(account.name);

    // Extract username/handle - handle both string and object formats
    const accountUsername = this.extractNestedValue(
      account.username ?? account.handle,
    );
    const usernameSearchType = this.extractOsintSearchType(
      account.username ?? account.handle,
    );

    const details = [
      this.createDetail(
        'Name',
        this.toFallbackString(accountName, 'N/A'),
        undefined,
        nameSearchType,
      ),
      this.createDetail(
        'Username',
        this.toFallbackString(accountUsername, 'N/A'),
        undefined,
        usernameSearchType,
      ),
      this.createDetail(
        'ID',
        this.toFallbackString(
          this.extractNestedValue(account.uid ?? account.id),
          'N/A',
        ),
      ),
      this.createDetail(
        'Profile URL',
        this.toFallbackString(
          this.extractNestedValue(
            account.profile_url ?? account.url ?? account.maps_url,
          ),
          'N/A',
        ),
      ),
    ].filter(Boolean);

    return this.buildEntry(
      'account',
      this.resolveEntryLabel(account, index),
      details,
    );
  }

  // Extract value from nested structure (handles both string and object formats)
  private extractNestedValue(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }
    // If it's an object with 'value' property, extract the value
    if (typeof value === 'object' && 'value' in value) {
      const extractedValue = value.value;
      // Handle null/undefined values in nested structure
      if (extractedValue === null || extractedValue === undefined) {
        return '';
      }
      return String(extractedValue);
    }
    // Otherwise, treat as string
    return String(value);
  }

  // Extract osint-search-type from nested structure
  private extractOsintSearchType(value: any): string | undefined {
    if (value === null || value === undefined) {
      return undefined;
    }
    // If it's an object with 'osint-search-type' property, extract it
    if (typeof value === 'object' && 'osint-search-type' in value) {
      return value['osint-search-type'];
    }
    return undefined;
  }

  private createInlineAccountEntry(platformData: any): any {
    // Extract name - handle both string and object formats, and arrays
    let name = this.extractNestedValue(platformData.name);
    if (!name && Array.isArray(platformData.names)) {
      // Handle array of names - extract values if they're objects
      const nameValues = platformData.names.map((n: any) =>
        this.extractNestedValue(n),
      );
      name = nameValues.filter((n: string) => n).join(', ');
    }

    // Extract handle - handle both string and object formats, and arrays
    let handle = this.extractNestedValue(platformData.username);
    if (!handle && Array.isArray(platformData.vpas)) {
      // Handle array of vpas - extract values if they're objects
      const vpaValues = platformData.vpas.map((v: any) =>
        this.extractNestedValue(v),
      );
      handle = vpaValues.filter((v: string) => v).join(', ');
    }

    const details = [
      this.createDetail('Name', this.toFallbackString(name, 'N/A')),
      this.createDetail('Username', this.toFallbackString(handle, 'N/A')),
      this.createDetail(
        'ID',
        this.toFallbackString(this.extractNestedValue(platformData.uid), 'N/A'),
      ),
      this.createDetail(
        'Profile URL',
        this.toFallbackString(
          this.extractNestedValue(
            platformData.profile_url ?? platformData.maps_url,
          ),
          'N/A',
        ),
      ),
    ].filter(Boolean);

    return this.buildEntry('account', 'Account Information', details);
  }

  private createNoAccountEntry(platformData: any): any {
    const details: any[] = [];

    if (
      Array.isArray(platformData.accounts) &&
      platformData.accounts.length === 0
    ) {
    }

    if (Array.isArray(platformData.names) && platformData.names.length > 0) {
      // Extract values from nested structures if needed
      const nameValues = platformData.names.map((n: any) =>
        this.extractNestedValue(n),
      );
      details.push(
        this.createDetail(
          'Names',
          nameValues.filter((n: string) => n).join(', '),
          'string',
        ),
      );
    }

    if (Array.isArray(platformData.vpas) && platformData.vpas.length > 0) {
      // Extract values from nested structures if needed
      const vpaValues = platformData.vpas.map((v: any) =>
        this.extractNestedValue(v),
      );
      details.push(
        this.createDetail(
          'VPAs',
          vpaValues.filter((v: string) => v).join(', '),
          'string',
        ),
      );
    }

    const uidValue = this.extractNestedValue(platformData.uid);
    if (uidValue) {
      details.push(
        this.createDetail('ID', this.toFallbackString(uidValue, 'N/A')),
      );
    }

    if (details.length > 0) {
      return this.buildEntry('account', 'No account found', details);
    }

    return {
      kind: 'account',
      label: 'No account found',
      details: [],
    };
  }

  private createAccountExistsEntry(_platformData: any): any {
    return this.buildEntry('account', 'Account exists', [
      this.createDetail('Status', 'Account found', 'string'),
    ]);
  }

  private createFieldEntries(fields: any): any[] {
    if (!fields || typeof fields !== 'object') {
      return [];
    }

    return Object.entries(fields)
      .filter(([fieldKey]) => !this.isHiddenDisplayField(fieldKey))
      .map(([fieldKey, rawField]) => {
        const field = this.normalizeField(rawField);
        if (!field) {
          return null;
        }

        const displayValue = this.normalizeFieldDisplayValue(fieldKey, field.value);
        const displayType = Array.isArray(displayValue)
          ? 'list'
          : typeof displayValue === 'object' && displayValue !== null
            ? 'object'
            : field.type;

        const detail = this.createDetail(
          displayLabelForFieldKey(fieldKey),
          displayValue,
          displayType,
          field['osint-search-type'],
        );

        return this.buildEntry('field', null, detail ? [detail] : []);
      })
      .filter((entry): entry is any => !!entry);
  }

  private buildImageResults(searchResult: any): any[] {
    const rawImages = searchResult?.image_url;
    if (!Array.isArray(rawImages)) {
      return [];
    }

    return rawImages
      .filter((url: any) => typeof url === 'string' && !!url.trim())
      .map((url: string, index: number) => {
        const matchedPlatform = this.findMatchingPlatform(url);
        const icon = createPlatformIconDataUrl(matchedPlatform || 'image');
        const platform = this.toImageSourceDisplayName(matchedPlatform);

        return {
          name: platform,
          src: url,
          height: index === 0 ? 200 : 150,
          icon,
          platform,
          detectUrl: url,
        };
      });
  }

  private toDisplayName(value: string | null | undefined): string {
    if (!value || typeof value !== 'string') {
      return 'Unknown';
    }

    const key = this.normalizeKey(value);
    const overrides: Record<string, string> = {
      'mobile-pan': 'Pan detail',
      mobile_pan: 'Pan detail',
      mobilepan: 'Pan detail',
      'iocl-subsidy': 'IndianOil LPG',
      iocl_subsidy: 'IndianOil LPG',
      ioclsubsidy: 'IndianOil LPG',
      num2google: 'Google',
      'num2-google': 'Google',
      num2_google: 'Google',
      tc_names: 'Names',
      'tc-names': 'Names',
    };
    if (overrides[key]) {
      return overrides[key];
    }

    // Multi-match cards stay separate, but the title is the platform only:
    // foursquare_1 / foursquare_2 → "Foursquare"
    const multiMatch = key.match(/^(.+)_(\d+)$/);
    if (multiMatch) {
      const baseKey = multiMatch[1];
      if (overrides[baseKey]) {
        return overrides[baseKey];
      }
      return baseKey
        .split(/[_\s-]+/)
        .filter(Boolean)
        .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
        .join(' ');
    }

    return value
      .split(/[_\s-]+/)
      .filter(Boolean)
      .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
      .join(' ');
  }

  private resolveIcon(platformKey: string): string {
    // Icons use base platform (foursquare_1 → foursquare)
    const baseKey = String(platformKey || '').replace(/_\d+$/, '');
    return createPlatformIconDataUrl(baseKey || platformKey);
  }

  private normalizeKey(key: string): string {
    return typeof key === 'string' ? key.trim().toLowerCase() : '';
  }

  /** Photo / vendor-metadata keys that should never appear as OSINT card rows. */
  isHiddenDisplayField(fieldKey: string): boolean {
    const key = canonicalFieldKey(fieldKey);
    return (
      key === 'picture_url' ||
      key === 'picture' ||
      key === 'image_hash' ||
      key === 'widgets' ||
      key === 'widget' ||
      key === 'map_data' ||
      key === 'map' ||
      key === 'category' ||
      key === 'category_name' ||
      key === 'category_type' ||
      key === 'category_information' ||
      // Same number as `phone`; vendors also send formatted and national copies.
      key === 'phone_display' ||
      key === 'national_number'
    );
  }

  /** Gallery badge: show Truecaller/Teams/etc., omit generic `new_api`. */
  private toImageSourceDisplayName(
    matchedPlatform: string | undefined,
  ): string {
    if (!matchedPlatform) {
      return 'Image';
    }
    if (this.normalizeKey(matchedPlatform) === 'new_api') {
      return '';
    }
    return this.toDisplayName(matchedPlatform);
  }

  private toFallbackString(value: any, fallback: string): string {
    if (value === undefined || value === null) {
      return fallback;
    }

    const coerced = String(value).trim();
    return coerced.length > 0 ? coerced : fallback;
  }

  private buildEntry(kind: string, label: string | null, details: any[]): any {
    const sanitizedDetails = (details ?? []).filter(
      (detail) => !!detail && detail.value !== undefined,
    );

    if (sanitizedDetails.length === 0) {
      return null;
    }

    return {
      kind,
      label,
      details: sanitizedDetails,
    };
  }

  private resolveEntryLabel(account: any, index?: number): string | null {
    // Extract values from nested structures
    const accountName = this.extractNestedValue(account?.name);
    const accountUsername = this.extractNestedValue(
      account?.username ?? account?.handle,
    );
    const accountUid = this.extractNestedValue(account?.uid ?? account?.id);

    const candidates = [accountName, accountUsername, accountUid]
      .map((value) =>
        typeof value === 'string' ? value.trim() : value ?? undefined,
      )
      .filter((value) => !!value);

    if (candidates.length > 0) {
      return String(candidates[0]);
    }

    if (typeof index === 'number') {
      return `Account ${index + 1}`;
    }

    return null;
  }

  private createDetail(
    label: string,
    value: any,
    explicitType?: string,
    osintSearchType?: string,
  ): any {
    if (value === undefined || value === null) {
      return null;
    }

    let resolvedValue = value;

    if (typeof resolvedValue === 'string') {
      resolvedValue = resolvedValue.trim();
      if (!resolvedValue && !explicitType) {
        return null;
      }
    }

    // Use explicitType if provided, otherwise detect from value
    const type = explicitType ?? this.detectValueType(resolvedValue);

    const detail: any = {
      label,
      value: resolvedValue,
      type,
    };

    // Preserve osint-search-type if provided
    if (osintSearchType) {
      detail['osint-search-type'] = osintSearchType;
    }

    return detail;
  }

  private detectValueType(value: any): string {
    if (typeof value === 'boolean') {
      return 'boolean';
    }

    if (typeof value === 'number') {
      return 'number';
    }

    if (Array.isArray(value)) {
      return 'list';
    }

    if (typeof value === 'object' && value !== null) {
      return 'object';
    }

    if (typeof value === 'string') {
      const trimmedValue = value.trim().toLowerCase();

      if (
        trimmedValue.startsWith('data:image/') &&
        trimmedValue.includes('base64,')
      ) {
        return 'base64String';
      }

      if (
        trimmedValue.startsWith('http://') ||
        trimmedValue.startsWith('https://')
      ) {
        return 'link';
      }

      // Check for datetime format (ISO 8601)
      if (
        /^\d{4}-\d{2}-\d{2}t\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:z|[+-]\d{2}:\d{2})?$/i.test(
          trimmedValue,
        )
      ) {
        return 'datetime';
      }

      // Check for date format (YYYY-MM-DD)
      if (/^\d{4}-\d{2}-\d{2}$/.test(trimmedValue)) {
        return 'date';
      }

      return 'string';
    }

    return 'string';
  }

  private normalizeField(field: any): any {
    if (!field || typeof field !== 'object') {
      return null;
    }
    // If field already has 'value' and 'type' properties (from API), use them
    if ('value' in field && 'type' in field) {
      // If type is 'dict' but value is an array, treat it as 'list'
      let resolvedType = field.type;
      if (field.type === 'dict' && Array.isArray(field.value)) {
        resolvedType = 'list';
      }
      // Use the API's type if available, but still validate the value
      const normalized: any = {
        value: field.value,
        type: resolvedType || this.detectValueType(field.value),
      };
      // Preserve osint-search-type if it exists
      if ('osint-search-type' in field) {
        normalized['osint-search-type'] = field['osint-search-type'];
      }
      return normalized;
    }
    // If only 'value' exists, detect type
    if ('value' in field) {
      const normalized: any = {
        value: field.value,
        type: this.detectValueType(field.value),
      };
      // Preserve osint-search-type if it exists
      if ('osint-search-type' in field) {
        normalized['osint-search-type'] = field['osint-search-type'];
      }
      return normalized;
    }
    // If only 'type' exists, use the field itself as value
    if ('type' in field) {
      const normalized: any = {
        value: field,
        type: field.type,
      };
      // Preserve osint-search-type if it exists
      if ('osint-search-type' in field) {
        normalized['osint-search-type'] = field['osint-search-type'];
      }
      return normalized;
    }
    // Fallback: treat the whole field as value
    const normalized: any = {
      value: field,
      type: this.detectValueType(field),
    };
    // Preserve osint-search-type if it exists
    if ('osint-search-type' in field) {
      normalized['osint-search-type'] = field['osint-search-type'];
    }
    return normalized;
  }

  private findMatchingPlatform(url: string): string | undefined {
    // Proxied OSINT images encode the source as a suffix: .../{hash}_{source}
    const fromUrlSuffix = this.extractPlatformFromImageUrlSuffix(url);
    if (fromUrlSuffix) {
      return fromUrlSuffix;
    }

    const lowerCaseUrl = url.toLowerCase();
    const candidates = [
      'facebook',
      'instagram',
      'twitter',
      'x.com',
      'skype',
      'telegram',
      'gpay',
      'imo',
      'whatsapp',
      'truecaller',
      'linkedin',
      'github',
      'youtube',
      'tiktok',
      'duolingo',
      'gravatar',
      'hackerearth',
      'adobe',
      'hibp',
      'google',
    ];
    return candidates.find((platform) => lowerCaseUrl.includes(platform));
  }

  /**
   * Extracts the source slug from OSINT image URLs / hashes shaped like
   * `1d09e30c5eb5cfbd0f552d764de4189b_hibp` or
   * `https://…/api/osint/image/1d09e30c5eb5cfbd0f552d764de4189b_hibp`.
   */
  private extractPlatformFromImageUrlSuffix(
    url: string,
  ): string | undefined {
    if (!url || typeof url !== 'string') {
      return undefined;
    }

    let filename = url.trim();
    try {
      filename = decodeURIComponent(new URL(url).pathname.split('/').pop() || '');
    } catch {
      filename = decodeURIComponent(url.split('/').pop() || url);
    }

    // Strip query/hash and common image extensions if present
    filename = filename.split('?')[0].split('#')[0];
    filename = filename.replace(
      /\.(png|jpe?g|gif|webp|svg|bmp|ico)$/i,
      '',
    );

    const match = filename.match(
      /^[a-f0-9]{16,}_([a-z0-9]+(?:_[a-z0-9]+)*)$/i,
    );
    return match?.[1]?.toLowerCase();
  }

  private isMeaningfulAccount(account: any): boolean {
    if (!account || typeof account !== 'object') {
      return false;
    }

    return (
      !!this.extractNestedValue(account.name) ||
      !!this.extractNestedValue(account.username) ||
      !!this.extractNestedValue(account.handle) ||
      !!this.extractNestedValue(account.uid) ||
      !!this.extractNestedValue(account.profile_url) ||
      !!this.extractNestedValue(account.url)
    );
  }

  private hasInlineAccountData(platformData: any): boolean {
    return (
      !!this.extractNestedValue(platformData.name) ||
      !!this.extractNestedValue(platformData.username) ||
      !!this.extractNestedValue(platformData.uid) ||
      (Array.isArray(platformData.names) && platformData.names.length > 0) ||
      (Array.isArray(platformData.vpas) && platformData.vpas.length > 0) ||
      !!this.extractNestedValue(platformData.profile_url) ||
      !!this.extractNestedValue(platformData.maps_url)
    );
  }

  private buildCarrierPlatforms(searchResult: any): any[] {
    const carrierPlatforms: any[] = [];

    // Process current_carrier
    if (
      searchResult?.current_carrier &&
      typeof searchResult.current_carrier === 'object'
    ) {
      const currentCarrierEntry = this.createCarrierEntry(
        'Current Carrier',
        searchResult.current_carrier,
      );
      if (currentCarrierEntry) {
        carrierPlatforms.push(currentCarrierEntry);
      }
    }

    // Process original_carrier
    if (
      searchResult?.original_carrier &&
      typeof searchResult.original_carrier === 'object'
    ) {
      const originalCarrierEntry = this.createCarrierEntry(
        'Original Carrier',
        searchResult.original_carrier,
      );
      if (originalCarrierEntry) {
        carrierPlatforms.push(originalCarrierEntry);
      }
    }

    return carrierPlatforms;
  }

  private createCarrierEntry(label: string, carrierData: any): any {
    if (!carrierData || typeof carrierData !== 'object') {
      return null;
    }

    const details: any[] = [];

    if (carrierData.name) {
      details.push(this.createDetail('Name', carrierData.name, 'string'));
    }

    if (carrierData.country) {
      details.push(this.createDetail('Country', carrierData.country, 'string'));
    }

    if (carrierData.network_code) {
      details.push(
        this.createDetail('Network Code', carrierData.network_code, 'string'),
      );
    }

    if (carrierData.network_type) {
      details.push(
        this.createDetail('Network Type', carrierData.network_type, 'string'),
      );
    }

    if (details.length === 0) {
      return null;
    }

    return {
      platform: label,
      icon: this.resolveIcon('carrier'),
      entries: [
        {
          kind: 'field',
          label: null,
          details,
        },
      ],
    };
  }

  private normalizeFieldDisplayValue(fieldKey: string, value: any): any {
    if (value === null || value === undefined) {
      return value;
    }

    if (typeof value === 'string') {
      const dateTimeParts = this.utilsService.splitDateTimeDisplay(value);
      if (dateTimeParts) {
        return dateTimeParts;
      }
    }

    if (Array.isArray(value)) {
      if (this.isLinkedAccountHintsField(fieldKey) || value.every((item) => this.isContactHintLike(item))) {
        return value
          .map((item) => this.formatContactHintLike(item))
          .filter((item: string) => item.length > 0);
      }

      // General rule: object list items → readable key/value strings
      return this.utilsService.normalizeListDisplayValue(value);
    }

    if (this.isLinkedAccountHintsField(fieldKey) && this.isContactHintLike(value)) {
      return this.formatContactHintLike(value);
    }

    return value;
  }

  private isLinkedAccountHintsField(fieldKey: string): boolean {
    const normalized = this.normalizeKey(fieldKey);
    return normalized.includes('linked') && normalized.includes('hint');
  }

  private isContactHintLike(value: any): boolean {
    return Boolean(
      value &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      (
        'contact_type' in value ||
        'is_obfuscated' in value ||
        'is_exact_length' in value ||
        ('kind' in value && 'value' in value)
      )
    );
  }

  private formatContactHintLike(value: any): string {
    if (!this.isContactHintLike(value)) {
      return typeof value === 'object'
        ? this.utilsService.formatKeyValueObject(value)
        : String(value ?? '');
    }

    const maskedValue = this.toFallbackString(value?.value, '').trim();
    const contactType = this.toFallbackString(value?.contact_type, '').trim();
    const verification =
      value?.verified === true
        ? 'verified'
        : value?.verified === false
          ? 'unverified'
          : '';

    const traits: string[] = [];

    if (value?.is_obfuscated) {
      traits.push('obfuscated');
    }

    if (value?.is_exact_length) {
      traits.push('exact length');
    }

    const baseParts = [contactType, maskedValue].filter(Boolean);
    const suffixParts = [verification, ...traits].filter(Boolean);

    if (baseParts.length === 0 && suffixParts.length === 0) {
      return '';
    }

    if (suffixParts.length === 0) {
      return baseParts.join(': ');
    }

    return `${baseParts.join(': ')} (${suffixParts.join(', ')})`;
  }
}
